import React, { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  X,
  RefreshCw,
  Mail,
  Shield,
  House,
  Calendar,
  UserCog,
} from "lucide-react";
import api, {
  ApiResponse,
  getLocationAdmins,
  createLocationAdmin,
  updateLocationAdmin,
  deleteLocationAdmin,
} from "../../services/api";
import { toast } from "react-hot-toast";
import { Hotel, LocationAdmin } from "../../utils";
import { useAuth } from "../../context/AuthContext";
const LocationAdminManagement: React.FC = () => {
  const [locationAdmins, setLocationAdmins] = useState<LocationAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const { user } = useAuth();

  // Form states
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    restaurantId: "",
  });
  const [editingAdmin, setEditingAdmin] = useState<LocationAdmin | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    fetchLocationAdmins();
    fetchHotels();
  }, []);

  const fetchLocationAdmins = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getLocationAdmins();
      if (response.data.success) {
        setLocationAdmins(response.data.data);
      } else {
        setLocationAdmins([]);
        toast.error("Failed to load location admins");
      }
    } catch (error) {
      toast.error("Failed to load location admins. Please try again.");
      setLocationAdmins([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchHotels = async () => {
    try {
      const res = await api.get<ApiResponse<Hotel[]>>(
        "/admin/getHotelInformation",
      );
      setHotels(res.data.data);
    } catch (err) {
      console.error("Failed to load hotels:", err);
      setHotels([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);
    setError(null);

    if (!formData.restaurantId) {
      toast.error("Please select a location");
      setFormLoading(false);
      return;
    }

    try {
      if (editingAdmin) {
        // Update location admin
        const payload: {
          email?: string;
          password?: string;
          restaurantId?: string;
        } = {};
        if (formData.email.trim()) payload.email = formData.email.trim();
        // Send the password exactly as typed (login compares it as typed)
        if (formData.password.trim()) payload.password = formData.password;
        if (formData.restaurantId) payload.restaurantId = formData.restaurantId;

        const response = await updateLocationAdmin(editingAdmin._id, payload);
        if (response.data.success) {
          toast.success("Location admin updated successfully!");
        } else {
          toast.error(response.data.error || "Update failed");
          setFormLoading(false);
          return;
        }
      } else {
        // Create new location admin
        if (!formData.email.trim() || !formData.password.trim()) {
          toast.error("Email and password are required");
          setFormLoading(false);
          return;
        }

        const response = await createLocationAdmin({
          email: formData.email.trim(),
          password: formData.password,
          restaurantId: formData.restaurantId,
        });

        if (response.data.success) {
          toast.success("Location admin created successfully!");
        } else {
          toast.error(response.data.error || "Creation failed");
          setFormLoading(false);
          return;
        }
      }

      resetForm();
      setShowModal(false);
      fetchLocationAdmins();
    } catch (error: any) {
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error(
          editingAdmin
            ? "Update failed. Please try again."
            : "Creation failed. Please try again.",
        );
      }
    } finally {
      setFormLoading(false);
    }
  };

  useEffect(() => {
    console.log("managment", user);
  }, []);
  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this location admin?"))
      return;

    try {
      setError(null);
      const response = await deleteLocationAdmin(id);
      if (response.data.success) {
        fetchLocationAdmins();
        toast.success("Location admin deleted successfully!");
      } else {
        toast.error(response.data.error || "Delete failed");
      }
    } catch (error: any) {
      setError("Failed to delete location admin. Please try again.");
      toast.error(
        error.response?.data?.error || "Failed to delete location admin.",
      );
    }
  };

  const handleEdit = (admin: LocationAdmin) => {
    setEditingAdmin(admin);
    setFormData({
      email: admin.email,
      password: "",
      restaurantId: admin.restaurantId?._id || "",
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingAdmin(null);
    setFormData({
      email: "",
      password: "",
      restaurantId: "",
    });
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  // Filter and pagination logic
  const filteredAdmins = locationAdmins.filter(
    (admin) =>
      admin.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (admin.restaurantId?.hotelName || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()),
  );

  const totalPages = Math.ceil(filteredAdmins.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedAdmins = filteredAdmins.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="relative max-w-7xl mx-auto p-4 pt-0 lg:p-8 overflow-y-auto no-scrollbar flex flex-col h-screen">
      {/* ================= HEADER ================= */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-sm py-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Location Admins</h1>

          <button
            onClick={openAddModal}
            className="w-auto self-start sm:self-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition inline-flex items-center gap-2 whitespace-nowrap">
            <Plus size={18} />
            Add Location Admin
          </button>
        </div>

        {/* ================= SEARCH ================= */}
        <div className="rounded-lg mb-6">
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Search Location Admins
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search by email or location..."
                className="pl-10 pr-4 py-2 w-full border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={fetchLocationAdmins}
              className="h-10 w-10 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition flex items-center justify-center"
              title="Refresh Location Admins"
            >
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
      </div>

      {/* ================= CONTENT ================= */}
      <div className="flex-1">
        {paginatedAdmins.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <p className="text-lg font-medium">No location admins found</p>
              <p className="text-sm mt-1">
                {searchTerm
                  ? "Try searching with a different term"
                  : "Add your first location admin to get started"}
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {paginatedAdmins.map((admin) => (
              <div
                key={admin._id}
                className="bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
              >
                {/* Admin Header */}
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <UserCog size={16} className="text-blue-600" />
                      <h3 className="text-xl font-bold text-gray-800 mb-0">
                        Location Admin
                      </h3>
                    </div>

                    <div className="flex items-center gap-2 mb-1">
                      <Mail size={14} className="text-gray-400" />
                      <p className="text-sm text-gray-600 mb-0">
                        {admin.email}
                      </p>
                    </div>

                    <div className="flex items-center gap-2 mb-2">
                      <Shield size={14} className="text-gray-400" />
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {admin.role}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 mb-2">
                      <House size={14} className="text-gray-400" />
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {admin.hotelName || "No location assigned"}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Calendar size={14} className="text-gray-400" />
                      <p className="text-xs text-gray-500 mb-0">
                        Created:{" "}
                        {new Date(admin.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(admin)}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition"
                  >
                    <Edit size={14} />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(admin._id)}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-red-600 text-white rounded text-sm hover:bg-red-700 transition"
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ================= PAGINATION ================= */}
      {filteredAdmins.length > 0 && totalPages > 1 && (
        <div className="py-4 flex justify-center">
          <div className="flex gap-2 items-center">
            {/* Previous Button */}
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50 hover:bg-gray-300 transition"
            >
              Prev
            </button>

            {/* Page Numbers */}
            {(() => {
              const pages = [];
              const showEllipsis = totalPages > 7;

              if (!showEllipsis) {
                for (let i = 1; i <= totalPages; i++) {
                  pages.push(
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i)}
                      className={`px-3 py-1 rounded transition ${
                        currentPage === i
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    >
                      {i}
                    </button>,
                  );
                }
              } else {
                const startPage = Math.max(1, currentPage - 2);
                const endPage = Math.min(totalPages, currentPage + 2);

                if (startPage > 1) {
                  pages.push(
                    <button
                      key={1}
                      onClick={() => setCurrentPage(1)}
                      className={`px-3 py-1 rounded transition ${
                        currentPage === 1
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    >
                      1
                    </button>,
                  );

                  if (startPage > 2) {
                    pages.push(
                      <span key="ellipsis1" className="px-2 py-1 text-gray-500">
                        ...
                      </span>,
                    );
                  }
                }

                for (let i = startPage; i <= endPage; i++) {
                  if (i !== 1 && i !== totalPages) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`px-3 py-1 rounded transition ${
                          currentPage === i
                            ? "bg-blue-600 text-white"
                            : "bg-gray-200 hover:bg-gray-300"
                        }`}
                      >
                        {i}
                      </button>,
                    );
                  }
                }

                if (endPage < totalPages) {
                  if (endPage < totalPages - 1) {
                    pages.push(
                      <span key="ellipsis2" className="px-2 py-1 text-gray-500">
                        ...
                      </span>,
                    );
                  }

                  pages.push(
                    <button
                      key={totalPages}
                      onClick={() => setCurrentPage(totalPages)}
                      className={`px-3 py-1 rounded transition ${
                        currentPage === totalPages
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    >
                      {totalPages}
                    </button>,
                  );
                }
              }

              return pages;
            })()}

            {/* Next Button */}
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50 hover:bg-gray-300 transition"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* ================= MODAL ================= */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ top: "0", left: "0", right: "0", bottom: "0" }}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => setShowModal(false)}
          />

          {/* Modal Container */}
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col mx-auto">
            {/* Modal Header - Fixed */}
            <div className="bg-white border-b border-gray-200 px-6 py-4 rounded-t-lg flex justify-between items-center flex-shrink-0">
              <h2 className="text-xl font-bold text-gray-800 mb-0">
                {editingAdmin
                  ? "Edit Location Admin"
                  : "Add New Location Admin"}
              </h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="flex-1 overflow-y-auto">
              <form
                id="location-admin-form"
                onSubmit={handleSubmit}
                className="p-6"
              >
                <div className="space-y-4">
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      placeholder="Enter admin email"
                      required
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Location *
                    </label>
                    <select
                      value={formData.restaurantId}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          restaurantId: e.target.value,
                        })
                      }
                      required
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
                    >
                      <option value="">Select a location</option>
                      {hotels.map((hotel) => (
                        <option key={hotel._id} value={hotel._id}>
                          {hotel.hotelName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Password{" "}
                      {editingAdmin ? "(leave blank to keep current)" : "*"}
                    </label>
                    <input
                      type="password"
                      // Stop Chrome filling in a saved login here, which would
                      // silently set this admin's password to that value
                      autoComplete="new-password"
                      name="location-admin-new-password"
                      value={formData.password}
                      onChange={(e) =>
                        setFormData({ ...formData, password: e.target.value })
                      }
                      placeholder={
                        editingAdmin
                          ? "Enter new password (optional)"
                          : "Enter password"
                      }
                      required={!editingAdmin}
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>
                </div>
              </form>
            </div>

            {/* Modal Footer - Fixed */}
            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 rounded-b-lg flex justify-end gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-3 py-2 sm:px-5 sm:py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="location-admin-form"
                className="px-3 py-2 sm:px-5 sm:py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
                disabled={formLoading}
              >
                {formLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin">...</span>
                    {editingAdmin ? "Updating..." : "Creating..."}
                  </span>
                ) : editingAdmin ? (
                  "Update Admin"
                ) : (
                  "Create Admin"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationAdminManagement;
